import { Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import SEO from "./SEO";
import { SERVICE_PAGES, servicePath } from "./servicePages";
import { CITY_PAGES, cityPath } from "./cityPages";
import "./styles/ServicePage.css";

const SITE = "https://atappliancemaster.com";
const PHONE = "(331) 600-4935";
const TEL = "+13316004935";

export default function ServicePage({ slug }) {
  const service = SERVICE_PAGES.find((s) => s.slug === slug);
  if (!service) return <Navigate to="/services" replace />;

  const path = servicePath(service.slug);
  const others = SERVICE_PAGES.filter((s) => s.slug !== service.slug);

  const graph = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        name: service.name,
        serviceType: service.name,
        description: service.metaDescription,
        url: `${SITE}${path}`,
        provider: {
          "@type": "HomeAndConstructionBusiness",
          name: "AT Appliance Repair and Maintenance LLC",
          url: SITE,
          telephone: TEL,
        },
        areaServed: CITY_PAGES.map((c) => ({ "@type": "City", name: `${c.name}, IL` })),
      },
      {
        "@type": "FAQPage",
        mainEntity: service.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
          { "@type": "ListItem", position: 2, name: "Services", item: `${SITE}/services` },
          { "@type": "ListItem", position: 3, name: service.name, item: `${SITE}${path}` },
        ],
      },
    ],
  };

  return (
    <main className="svc-page">
      <SEO
        path={path}
        title={`${service.name} in Chicago Suburbs, IL | AT Appliance Repair`}
        description={service.metaDescription}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(graph)}</script>
      </Helmet>

      <div className="svc-crumbs">
        <Link to="/">Home</Link> / <Link to="/services">Services</Link> / {service.name}
      </div>

      <h1>{service.name} in the Chicago Suburbs</h1>
      <p>{service.intro}</p>

      <div className="svc-cta">
        <a className="svc-btn" href={`tel:${TEL}`}>Call {PHONE}</a>
        <Link className="svc-btn svc-btn-alt" to="/contact">Schedule Service</Link>
      </div>

      <h2>Common {service.name.toLowerCase()} problems we fix</h2>
      <div className="svc-grid">
        {service.problems.map((p) => (
          <div className="svc-card" key={p.title}>
            <h3>{p.title}</h3>
            <p>{p.text}</p>
          </div>
        ))}
      </div>

      <h2>What to expect</h2>
      <ul className="svc-list">
        <li>A qualified technician inspects and diagnoses the problem. The service fee is $79.</li>
        <li>You get a quote for parts and labor before any repair starts.</li>
        <li>If you go ahead with the repair, the service fee is waived.</li>
        <li>Same-day service is available in most cases.</li>
        <li>Repairs come with a 30-day parts and labor warranty.</li>
      </ul>

      {service.brands.length > 0 && (
        <>
          <h2>Brands we repair</h2>
          <ul className="svc-chips">
            {service.brands.map((b) => <li key={b}>{b}</li>)}
          </ul>
        </>
      )}

      <h2>Frequently asked questions</h2>
      <div className="svc-faq">
        {service.faqs.map((f) => (
          <details key={f.q}>
            <summary>{f.q}</summary>
            <p>{f.a}</p>
          </details>
        ))}
      </div>

      <h2>Cities we serve</h2>
      <ul className="svc-cities">
        {CITY_PAGES.map((c) => (
          <li key={c.slug}><Link to={cityPath(c.slug)}>{c.name}, IL</Link></li>
        ))}
      </ul>

      <h2>Other appliance repair services</h2>
      <ul className="svc-list">
        {others.map((s) => (
          <li key={s.slug}><Link to={servicePath(s.slug)}>{s.name}</Link></li>
        ))}
      </ul>
    </main>
  );
}